// Global notice bus for the rider-config anti-data-loss guards (2026-10-07).
//
// Two situations must never be silent:
//   - 'conflict'  : the save was REFUSED because another session wrote first
//                   (HTTP 409 `stale`); the client reloaded the latest version.
//   - 'preserved' : the server KEPT a stored value this session did not edit
//                   (non-destructive merge) instead of taking the emptied one.
//
// <ConfigGuardBanner/> (rendered once in App.tsx) subscribes to this bus and
// shows a visible, dismissible banner. UI text is 100% English.

import i18n from '../i18n';

export type ConfigNoticeKind = 'conflict' | 'preserved';

export interface ConfigNotice {
  id: number;
  kind: ConfigNoticeKind;
  message: string;
  /** For a `preserved` notice: the section the server kept (e.g.
   *  `stageDesign.van`). The banner composes the translated sentence from it. */
  section?: string;
}

let seq = 0;
let notices: ConfigNotice[] = [];
const listeners = new Set<(n: ConfigNotice[]) => void>();

function emit() {
  const snapshot = [...notices];
  listeners.forEach((l) => l(snapshot));
}

export function pushConfigNotice(kind: ConfigNoticeKind, message: string, section?: string): void {
  // De-duplicate an identical kind+message so a repeated autosave cannot stack
  // the same sentence several times.
  if (notices.some((n) => n.kind === kind && n.message === message)) return;
  notices.push({ id: (seq += 1), kind, message, ...(section ? { section } : {}) });
  emit();
}

export function clearConfigNotices(): void {
  notices = [];
  emit();
}

export function dismissConfigNotice(id: number): void {
  notices = notices.filter((n) => n.id !== id);
  emit();
}

export function subscribeConfigNotices(cb: (n: ConfigNotice[]) => void): () => void {
  listeners.add(cb);
  cb([...notices]);
  return () => {
    listeners.delete(cb);
  };
}

/** The exact copy shown when a save was refused (409 stale). Sourced from the
 *  i18n catalogue (key `configGuard.conflict`) so the same sentence is one
 *  translatable resource; the English value is unchanged. Kept exported so the
 *  hook and the QA script can assert on one constant. */
export const CONFIG_CONFLICT_MESSAGE = i18n.t('configGuard.conflict');
